import { Link } from 'react-router-dom';
import type { Ticket } from '@/api/client';
import { cn } from '@/lib/utils';
import { fmtRelative, dueState, fmtDate } from '@/lib/cobalt-helpers';
import { CompanyMark } from '@/components/CompanyMark';
import { StatusBadge } from '@/components/StatusBadge';
import { PriorityTag } from '@/components/PriorityTag';
import { statusKind } from '@/lib/cobalt-helpers';
import { ticketPath } from '@/lib/routes';

interface Props {
  t: Ticket;
  companyMap: Map<number, string>;
  statusMap: Map<number, string>;
  priorityMap: Map<number, string>;
  queueMap: Map<number, string>;
}

/**
 * Mobile ticket card — drop-in replacement for the inline TicketCard inside
 * TicketsView.tsx. Title now leads (was the # ID); status, priority and due
 * are semantic chips, not gray pills; overdue rows get a left accent rail.
 */
export function TicketCard({ t, companyMap, statusMap, priorityMap, queueMap }: Props) {
  const label    = t.ticketNumber || String(t.id);
  const company  = t.companyID != null ? companyMap.get(t.companyID)  ?? `#${t.companyID}` : null;
  const status   = t.status   != null ? statusMap.get(t.status)       : null;
  const priority = t.priority != null ? priorityMap.get(t.priority)   : null;
  const queue    = t.queueID  != null ? queueMap.get(t.queueID)       : null;

  const ds = dueState(t.dueDateTime);
  const dueLabel = !t.dueDateTime ? null
    : ds === 'overdue' ? `Overdue · ${fmtDate(t.dueDateTime)}`
    : ds === 'soon'    ? `Due · ${fmtDate(t.dueDateTime)}`
    :                    `Due ${fmtDate(t.dueDateTime)}`;
  const dueKind = ds === 'overdue' ? 'destructive' : ds === 'soon' ? 'warning' : 'neutral';

  return (
    <Link
      to={ticketPath(t.id)}
      className={cn(
        'block rounded-md border border-border bg-card px-3.5 py-3 shadow-sm transition-colors',
        'hover:bg-muted/40 active:bg-muted/60',
        ds === 'overdue' && 'rail-danger',
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-[11px] tracking-wider text-brand-strong">{label}</span>
        <span className="shrink-0 text-[11px] text-muted-foreground">{fmtRelative(t.lastActivityDate)}</span>
      </div>
      <div className="mt-1 line-clamp-2 text-[14.5px] font-[550] leading-snug tracking-[-0.005em] text-foreground">
        {t.title}
      </div>
      {company && (
        <div className="mt-2 flex items-center gap-2">
          <CompanyMark name={company} size={18}/>
          <span className="truncate text-xs text-foreground/80">{company}</span>
        </div>
      )}
      <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
        {status   && <StatusBadge kind={statusKind(status)} dot>{status}</StatusBadge>}
        {priority && <PriorityTag priority={priority} compact/>}
        {queue    && <span className="text-[11.5px] text-muted-foreground">{queue}</span>}
        {dueLabel && (
          <span className="ml-auto">
            <StatusBadge kind={dueKind} dot={ds !== 'normal'}>{dueLabel}</StatusBadge>
          </span>
        )}
      </div>
    </Link>
  );
}
